import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiCreditCard } from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { colors, borderRadius, shadows } from '../../theme/colors';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import {
  PK_BANKS, CARD_TYPES, WEEK_DAYS, DEFAULT_SHOP_SHARE,
  ensureBankDiscountAccounts, offerUnavailableReason, offerLabel
} from '../../utils/bankDiscountHelper';
import { localDateStr } from '../../utils/localDate';

const EMPTY = {
  bankName: '', cardType: '', cardBins: '',
  discountPercent: '', capPerBill: '', minBill: '',
  maxUsesPerDay: '', maxUsesPerMonth: '', maxDiscountPerMonth: '',
  validFrom: '', validTo: '', daysOfWeek: [], timeFrom: '', timeTo: '',
  branchIds: [], shopSharePct: DEFAULT_SHOP_SHARE, isActive: true, notes: ''
};

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const showNum = (v) => (v === null || v === undefined ? '' : String(v));

const BankDiscounts = () => {
  const { activeCompany, currentUser, branches } = useAuth();
  const companyId = activeCompany?.id;
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'banks');

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  useEscapeClose(showModal, () => setShowModal(false));

  const branchList = useMemo(() => (branches || []).filter(b => b && b.id), [branches]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'companies', companyId, 'bankDiscounts'));
      setOffers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading bank discounts:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q ? offers.filter(o => offerLabel(o).toLowerCase().includes(q)) : offers;
    return [...rows].sort((a, b) => offerLabel(a).localeCompare(offerLabel(b)));
  }, [offers, search]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, validFrom: localDateStr() });
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (o) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditing(o);
    setForm({
      ...EMPTY,
      bankName: o.bankName || '', cardType: o.cardType || '', cardBins: o.cardBins || '',
      discountPercent: showNum(o.discountPercent), capPerBill: showNum(o.capPerBill), minBill: showNum(o.minBill),
      maxUsesPerDay: showNum(o.maxUsesPerDay), maxUsesPerMonth: showNum(o.maxUsesPerMonth),
      maxDiscountPerMonth: showNum(o.maxDiscountPerMonth),
      validFrom: o.validFrom || '', validTo: o.validTo || '',
      daysOfWeek: Array.isArray(o.daysOfWeek) ? o.daysOfWeek : [],
      timeFrom: o.timeFrom || '', timeTo: o.timeTo || '',
      branchIds: Array.isArray(o.branchIds) ? o.branchIds : [],
      shopSharePct: o.shopSharePct ?? DEFAULT_SHOP_SHARE,
      isActive: o.isActive !== false, notes: o.notes || ''
    });
    setFormError('');
    setShowModal(true);
  };

  const set = (k, v) => { setFormError(''); setForm(prev => ({ ...prev, [k]: v })); };
  const toggleIn = (k, v) => setForm(prev => {
    const arr = Array.isArray(prev[k]) ? prev[k] : [];
    return { ...prev, [k]: arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v] };
  });

  const validate = () => {
    if (!form.bankName.trim()) return 'Enter the bank name.';
    if (!form.cardType.trim()) return 'Enter the card type (e.g. Credit Card).';
    const pct = Number(form.discountPercent);
    if (!(pct > 0) || pct > 100) return 'Discount % must be between 0 and 100.';
    const shop = Number(form.shopSharePct);
    if (!(shop >= 0) || shop > 100) return 'Shop share % must be between 0 and 100.';
    for (const [k, label] of [['capPerBill', 'Max discount per bill'], ['minBill', 'Minimum bill'],
      ['maxUsesPerDay', 'Uses per day'], ['maxUsesPerMonth', 'Uses per month'], ['maxDiscountPerMonth', 'Max discount per month']]) {
      if (form[k] !== '' && !(Number(form[k]) >= 0)) return `${label} must be a positive number or blank.`;
    }
    if (!form.validFrom) return 'Enter the "Valid from" date.';
    if (form.validTo && form.validTo < form.validFrom) return '"Valid to" is before "Valid from".';
    if ((form.timeFrom && !form.timeTo) || (!form.timeFrom && form.timeTo)) return 'Enter both times, or leave both blank.';
    const bins = String(form.cardBins || '').split(/[\s,]+/).filter(Boolean);
    if (bins.some(b => !/^\d{4,8}$/.test(b))) return 'Card first digits must be numbers (e.g. 457123, 512345).';
    return '';
  };

  const handleSave = async () => {
    if (!canEdit) { setFormError('You do not have permission to change bank discounts.'); return; }
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true);
    setFormError('');
    try {
      // Claim (per bank) + recovery accounts — created on first use, reused after.
      const { claimAccount, recoveryAccount } = await ensureBankDiscountAccounts(companyId, form.bankName, currentUser?.uid);
      const shop = Math.round(Number(form.shopSharePct) * 100) / 100;
      const data = {
        bankName: form.bankName.trim(),
        cardType: form.cardType.trim(),
        cardBins: String(form.cardBins || '').split(/[\s,]+/).filter(Boolean).join(', '),
        discountPercent: Number(form.discountPercent),
        capPerBill: numOrNull(form.capPerBill),
        minBill: numOrNull(form.minBill),
        maxUsesPerDay: numOrNull(form.maxUsesPerDay),
        maxUsesPerMonth: numOrNull(form.maxUsesPerMonth),
        maxDiscountPerMonth: numOrNull(form.maxDiscountPerMonth),
        validFrom: form.validFrom,
        validTo: form.validTo || null,
        daysOfWeek: [...form.daysOfWeek].sort(),
        timeFrom: form.timeFrom || null,
        timeTo: form.timeTo || null,
        branchIds: form.branchIds,
        shopSharePct: shop,
        bankSharePct: Math.round((100 - shop) * 100) / 100,
        isActive: !!form.isActive,
        notes: form.notes || '',
        claimAccountId: claimAccount.id, claimAccountCode: claimAccount.code, claimAccountName: claimAccount.name,
        recoveryAccountId: recoveryAccount.id, recoveryAccountCode: recoveryAccount.code, recoveryAccountName: recoveryAccount.name,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid || null
      };
      if (editing) {
        await updateDoc(doc(db, 'companies', companyId, 'bankDiscounts', editing.id), data);
      } else {
        await addDoc(collection(db, 'companies', companyId, 'bankDiscounts'), {
          ...data, createdAt: data.updatedAt, createdBy: currentUser?.uid || null
        });
      }
      setShowModal(false);
      await load();
    } catch (e) {
      console.error('Error saving bank discount:', e);
      const denied = e?.code === 'permission-denied' || /insufficient permissions/i.test(e?.message || '');
      setFormError(denied
        ? 'Saving the first offer of a bank creates its claim account in the Chart of Accounts, which needs Chart of Accounts edit permission. Ask an admin to save it once.'
        : (e.message || 'Could not save. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (o) => {
    if (!canEdit) return;
    try {
      await updateDoc(doc(db, 'companies', companyId, 'bankDiscounts', o.id), {
        isActive: o.isActive === false, updatedAt: new Date().toISOString(), updatedBy: currentUser?.uid || null
      });
      setOffers(prev => prev.map(x => (x.id === o.id ? { ...x, isActive: o.isActive === false } : x)));
    } catch (e) {
      alert('Could not update: ' + (e.message || e));
    }
  };

  const handleDelete = async (o) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete the offer "${offerLabel(o)} ${o.discountPercent}%"?\n\nPast sales and the Bank Discount report keep their records. To pause an offer instead, switch it off.`)) return;
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'bankDiscounts', o.id));
      setOffers(prev => prev.filter(x => x.id !== o.id));
    } catch (e) {
      alert('Could not delete (only an admin can delete). You can switch the offer off instead.\n\n' + (e.message || e));
    }
  };

  const limitText = (o) => {
    const bits = [];
    if (Number(o.maxUsesPerDay) > 0) bits.push(`${o.maxUsesPerDay}/day`);
    if (Number(o.maxUsesPerMonth) > 0) bits.push(`${o.maxUsesPerMonth}/month`);
    if (Number(o.maxDiscountPerMonth) > 0) bits.push(`max ${Number(o.maxDiscountPerMonth).toLocaleString()}/month`);
    return bits.length ? bits.join(' · ') : 'No limit';
  };
  const validText = (o) => {
    const bits = [`${o.validFrom || '—'} → ${o.validTo || 'open'}`];
    if (Array.isArray(o.daysOfWeek) && o.daysOfWeek.length) bits.push(o.daysOfWeek.map(d => WEEK_DAYS[d]).join(', '));
    if (o.timeFrom && o.timeTo) bits.push(`${o.timeFrom}–${o.timeTo}`);
    return bits.join(' · ');
  };

  const shopShare = Number(form.shopSharePct);
  const bankShare = Number.isFinite(shopShare) ? Math.round((100 - shopShare) * 100) / 100 : '';

  return (
    <PageLayout pageTitle="Bank Discounts" breadcrumb="Maintain / Bank Discounts">
      <Container>
        <HelpBox>
          <strong>Bank card discounts at the POS.</strong> Add each bank's card offer here. At the POS, when the customer
          pays by <b>Card</b>, the cashier picks the offer and types the card's first 6 and last 4 digits — the discount is
          applied automatically (before GST) if the card's limits allow. The bank's share is booked as a claim on that bank
          under <b>Bank Discount Claims</b>; when the bank pays, record a normal Bank Receipt against that account.
        </HelpBox>

        <HeaderRow>
          <SearchBox>
            <FiSearch />
            <input placeholder="Search bank or card type…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <ClearBtn onClick={() => setSearch('')}><FiX /></ClearBtn>}
          </SearchBox>
          {canEdit && <AddBtn onClick={openAdd}><FiPlus /> Add Bank Discount</AddBtn>}
        </HeaderRow>

        {loading ? (
          <Muted>Loading…</Muted>
        ) : filtered.length === 0 ? (
          <Empty>
            <FiCreditCard size={34} />
            <div>{search ? 'No offers match your search.' : 'No bank discounts yet. Click "Add Bank Discount".'}</div>
          </Empty>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Bank / Card</Th>
                  <Th $right>Discount</Th>
                  <Th $right>Max / Bill</Th>
                  <Th $right>Min Bill</Th>
                  <Th>Per-card limit</Th>
                  <Th>Validity</Th>
                  <Th>Shop / Bank</Th>
                  <Th>Status</Th>
                  <Th $right>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const reason = offerUnavailableReason({ ...o, isActive: true });
                  return (
                    <tr key={o.id}>
                      <Td><b>{o.bankName}</b><Sub>{o.cardType}{o.cardBins ? ` · ${o.cardBins}` : ''}</Sub></Td>
                      <Td $right><b>{o.discountPercent}%</b></Td>
                      <Td $right>{Number(o.capPerBill) > 0 ? Number(o.capPerBill).toLocaleString() : '—'}</Td>
                      <Td $right>{Number(o.minBill) > 0 ? Number(o.minBill).toLocaleString() : '—'}</Td>
                      <Td>{limitText(o)}</Td>
                      <Td>{validText(o)}{reason && o.isActive !== false && <Warn>{reason}</Warn>}</Td>
                      <Td>{o.shopSharePct ?? DEFAULT_SHOP_SHARE}% / {o.bankSharePct ?? (100 - DEFAULT_SHOP_SHARE)}%</Td>
                      <Td>
                        <StatusBtn type="button" $on={o.isActive !== false} onClick={() => handleToggleActive(o)} disabled={!canEdit}
                          title={canEdit ? 'Click to switch on/off' : ''}>
                          {o.isActive !== false ? 'Active' : 'Off'}
                        </StatusBtn>
                      </Td>
                      <Td $right>
                        <Actions>
                          {canChange && <IconBtn onClick={() => openEdit(o)} title="Edit"><FiEdit2 /></IconBtn>}
                          {canDelete && <IconBtn $danger onClick={() => handleDelete(o)} title="Delete"><FiTrash2 /></IconBtn>}
                        </Actions>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Container>

      {showModal && (
        <Overlay onMouseDown={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <Modal>
            <ModalHead>
              <h3>{editing ? 'Edit Bank Discount' : 'Add Bank Discount'}</h3>
              <ClearBtn onClick={() => setShowModal(false)}><FiX size={18} /></ClearBtn>
            </ModalHead>
            <ModalBody>
              <Section>Card</Section>
              <Grid>
                <Field>
                  <Lbl>Bank name *</Lbl>
                  <Inp list="bd-banks" value={form.bankName} onChange={e => set('bankName', e.target.value)} placeholder="e.g. HBL" />
                  <datalist id="bd-banks">{PK_BANKS.map(b => <option key={b} value={b} />)}</datalist>
                </Field>
                <Field>
                  <Lbl>Card type *</Lbl>
                  <Inp list="bd-cards" value={form.cardType} onChange={e => set('cardType', e.target.value)} placeholder="e.g. Credit Card" />
                  <datalist id="bd-cards">{CARD_TYPES.map(c => <option key={c} value={c} />)}</datalist>
                </Field>
                <Field $span2>
                  <Lbl>Card first digits (optional)</Lbl>
                  <Inp value={form.cardBins} onChange={e => set('cardBins', e.target.value)} placeholder="e.g. 457123, 512345 — blank = any card of this bank" />
                  <Hint>If filled, the POS accepts only cards starting with these digits.</Hint>
                </Field>
              </Grid>

              <Section>Discount</Section>
              <Grid>
                <Field>
                  <Lbl>Discount % *</Lbl>
                  <Inp type="number" min="0" max="100" step="0.01" value={form.discountPercent} onChange={e => set('discountPercent', e.target.value)} />
                </Field>
                <Field>
                  <Lbl>Max discount per bill</Lbl>
                  <Inp type="number" min="0" step="0.01" value={form.capPerBill} onChange={e => set('capPerBill', e.target.value)} placeholder="blank = no cap" />
                </Field>
                <Field>
                  <Lbl>Minimum bill</Lbl>
                  <Inp type="number" min="0" step="0.01" value={form.minBill} onChange={e => set('minBill', e.target.value)} placeholder="blank = any bill" />
                </Field>
                <Field>
                  <Lbl>Shop share % / Bank share %</Lbl>
                  <ShareRow>
                    <Inp type="number" min="0" max="100" step="0.01" value={form.shopSharePct} onChange={e => set('shopSharePct', e.target.value)} />
                    <ShareBank>Bank {bankShare === '' ? '—' : `${bankShare}%`}</ShareBank>
                  </ShareRow>
                </Field>
              </Grid>

              <Section>Limit for one card</Section>
              <Grid>
                <Field>
                  <Lbl>Uses per day</Lbl>
                  <Inp type="number" min="0" step="1" value={form.maxUsesPerDay} onChange={e => set('maxUsesPerDay', e.target.value)} placeholder="blank = no limit" />
                </Field>
                <Field>
                  <Lbl>Uses per month</Lbl>
                  <Inp type="number" min="0" step="1" value={form.maxUsesPerMonth} onChange={e => set('maxUsesPerMonth', e.target.value)} placeholder="blank = no limit" />
                </Field>
                <Field>
                  <Lbl>Max discount per month</Lbl>
                  <Inp type="number" min="0" step="0.01" value={form.maxDiscountPerMonth} onChange={e => set('maxDiscountPerMonth', e.target.value)} placeholder="blank = no limit" />
                </Field>
              </Grid>

              <Section>Validity</Section>
              <Grid>
                <Field>
                  <Lbl>Valid from *</Lbl>
                  <Inp type="date" value={form.validFrom} onChange={e => set('validFrom', e.target.value)} />
                </Field>
                <Field>
                  <Lbl>Valid to</Lbl>
                  <Inp type="date" value={form.validTo} onChange={e => set('validTo', e.target.value)} />
                </Field>
                <Field>
                  <Lbl>Time from</Lbl>
                  <Inp type="time" value={form.timeFrom} onChange={e => set('timeFrom', e.target.value)} />
                </Field>
                <Field>
                  <Lbl>Time to</Lbl>
                  <Inp type="time" value={form.timeTo} onChange={e => set('timeTo', e.target.value)} />
                </Field>
                <Field $span2>
                  <Lbl>Days (none ticked = every day)</Lbl>
                  <Chips>
                    {WEEK_DAYS.map((d, i) => (
                      <ChipBtn key={d} type="button" $on={form.daysOfWeek.includes(i)} onClick={() => toggleIn('daysOfWeek', i)}>{d}</ChipBtn>
                    ))}
                  </Chips>
                </Field>
                {branchList.length > 1 && (
                  <Field $span2>
                    <Lbl>Branches (none ticked = all branches)</Lbl>
                    <Chips>
                      {branchList.map(b => (
                        <ChipBtn key={b.id} type="button" $on={form.branchIds.includes(b.id)} onClick={() => toggleIn('branchIds', b.id)}>
                          {b.branchName || b.name || b.branchCode || b.id}
                        </ChipBtn>
                      ))}
                    </Chips>
                  </Field>
                )}
              </Grid>

              <Grid>
                <Field $span2>
                  <Lbl>Notes</Lbl>
                  <Inp value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="e.g. Bank campaign reference" />
                </Field>
                <Field>
                  <Lbl>Status</Lbl>
                  <CheckRow>
                    <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} id="bd-active" />
                    <label htmlFor="bd-active">Active</label>
                  </CheckRow>
                </Field>
              </Grid>

              {formError && <ErrBox>{formError}</ErrBox>}
            </ModalBody>
            <ModalFoot>
              <Btn type="button" onClick={() => setShowModal(false)}>Cancel</Btn>
              <Btn type="button" $primary onClick={handleSave} disabled={saving || !canEdit}>{saving ? 'Saving…' : 'Save'}</Btn>
            </ModalFoot>
          </Modal>
        </Overlay>
      )}
    </PageLayout>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────
const Container = styled.div`display: flex; flex-direction: column; gap: 14px;`;
const HelpBox = styled.div`
  background: #F5F8FF; border: 1px solid #DCE6FF; color: ${colors.text.secondary};
  border-radius: ${borderRadius.md}; padding: 12px 14px; font-size: 13px; line-height: 1.55;
  & strong, & b { color: ${colors.text.primary}; }
`;
const HeaderRow = styled.div`display: flex; gap: 10px; align-items: center; flex-wrap: wrap;`;
const SearchBox = styled.div`
  flex: 1; min-width: 220px; display: flex; align-items: center; gap: 8px;
  background: white; border: 1px solid ${colors.neutral[300]}; border-radius: ${borderRadius.md}; padding: 8px 12px;
  & input { border: none; outline: none; flex: 1; font-size: 14px; }
  & > svg { color: ${colors.text.tertiary}; }
`;
const ClearBtn = styled.button`
  background: none; border: none; cursor: pointer; color: ${colors.text.secondary};
  display: inline-flex; align-items: center; padding: 2px;
`;
const AddBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px;
  background: ${colors.primary.main}; color: white; border: none; border-radius: ${borderRadius.md};
  font-weight: 600; font-size: 14px; cursor: pointer;
  &:hover { filter: brightness(0.92); }
`;
const Muted = styled.div`padding: 30px; text-align: center; color: ${colors.text.secondary};`;
const Empty = styled.div`
  padding: 40px; text-align: center; color: ${colors.text.secondary}; background: white;
  border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm}; display: flex; flex-direction: column; gap: 10px; align-items: center;
`;
const TableWrap = styled.div`background: white; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm}; overflow-x: auto;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  text-align: ${p => (p.$right ? 'right' : 'left')}; padding: 11px 14px; font-size: 12px; font-weight: 600;
  color: ${colors.text.secondary}; text-transform: uppercase; background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]}; white-space: nowrap;
`;
const Td = styled.td`
  text-align: ${p => (p.$right ? 'right' : 'left')}; padding: 11px 14px; font-size: 13.5px;
  border-bottom: 1px solid ${colors.neutral[100]}; vertical-align: top; color: ${colors.text.primary};
`;
const Sub = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin-top: 2px;`;
const Warn = styled.div`font-size: 11.5px; color: #B45309; margin-top: 3px; font-weight: 600;`;
const StatusBtn = styled.button`
  padding: 3px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => (p.$on ? '#1B8E5A' : colors.neutral[300])};
  background: ${p => (p.$on ? '#E8F5E9' : colors.neutral[100])};
  color: ${p => (p.$on ? '#1B8E5A' : colors.text.secondary)};
  &:disabled { cursor: default; }
`;
const Actions = styled.div`display: inline-flex; gap: 6px;`;
const IconBtn = styled.button`
  width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid ${colors.neutral[200]}; background: white; border-radius: ${borderRadius.sm}; cursor: pointer;
  color: ${p => (p.$danger ? colors.error.main : colors.text.secondary)};
  &:hover { border-color: ${p => (p.$danger ? colors.error.main : colors.primary.main)}; }
`;
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000;
  display: flex; align-items: flex-start; justify-content: center; padding: 30px 12px; overflow-y: auto;
`;
const Modal = styled.div`
  background: white; border-radius: ${borderRadius.lg}; width: 100%; max-width: 760px; box-shadow: ${shadows.lg};
`;
const ModalHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding: 14px 18px;
  border-bottom: 1px solid ${colors.neutral[200]};
  & h3 { margin: 0; font-size: 17px; color: ${colors.text.primary}; }
`;
const ModalBody = styled.div`padding: 6px 18px 14px;`;
const ModalFoot = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; padding: 12px 18px; border-top: 1px solid ${colors.neutral[200]};
`;
const Section = styled.div`
  margin: 14px 0 8px; font-size: 12px; font-weight: 700; color: ${colors.primary.main};
  text-transform: uppercase; letter-spacing: 0.5px;
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px;
`;
const Field = styled.div`
  display: flex; flex-direction: column; gap: 5px;
  ${p => (p.$span2 ? 'grid-column: 1 / -1;' : '')}
`;
const Lbl = styled.label`font-size: 12px; font-weight: 600; color: ${colors.text.secondary};`;
const Hint = styled.div`font-size: 11.5px; color: ${colors.text.tertiary};`;
const Inp = styled.input`
  padding: 8px 10px; font-size: 14px; border: 1px solid ${colors.neutral[300]}; border-radius: ${borderRadius.md};
  outline: none; width: 100%; box-sizing: border-box;
  &:focus { border-color: ${colors.primary.main}; }
`;
const ShareRow = styled.div`display: flex; align-items: center; gap: 8px;`;
const ShareBank = styled.div`
  white-space: nowrap; font-size: 13px; font-weight: 600; color: #1E40AF; background: #EEF3FF;
  padding: 8px 10px; border-radius: ${borderRadius.md};
`;
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const ChipBtn = styled.button`
  padding: 6px 12px; border-radius: 16px; font-size: 12.5px; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => (p.$on ? colors.primary.main : colors.neutral[300])};
  background: ${p => (p.$on ? `${colors.primary.main}12` : 'white')};
  color: ${p => (p.$on ? colors.primary.main : colors.text.secondary)};
`;
const CheckRow = styled.div`display: flex; align-items: center; gap: 8px; padding-top: 8px; font-size: 14px;`;
const ErrBox = styled.div`
  margin-top: 12px; padding: 9px 12px; background: #FFEBEE; color: ${colors.error.main};
  border-radius: ${borderRadius.md}; font-size: 13px; font-weight: 600;
`;
const Btn = styled.button`
  padding: 9px 18px; border-radius: ${borderRadius.md}; font-size: 14px; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => (p.$primary ? colors.primary.main : colors.neutral[300])};
  background: ${p => (p.$primary ? colors.primary.main : 'white')};
  color: ${p => (p.$primary ? 'white' : colors.text.primary)};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export default BankDiscounts;
